import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {
  FeedbackDatePreset,
  FeedbackFilterState,
  FEEDBACK_DATE_PRESET_OPTIONS,
  getRangeForPreset,
  toDateInputValue,
} from './feedbackDateRange'

type Props = {
  showFilter: boolean
  toggle: () => void
  activeFilter: FeedbackFilterState
  onApply: (filter: FeedbackFilterState) => void
}

const FeedbackFilter = ({showFilter, toggle, activeFilter, onApply}: Props) => {
  const [preset, setPreset] = useState<FeedbackDatePreset>(activeFilter.preset)
  const [customFrom, setCustomFrom] = useState(activeFilter.date_from)
  const [customTo, setCustomTo] = useState(activeFilter.date_to)
  const [rangeError, setRangeError] = useState('')

  useEffect(() => {
    if (!showFilter) return
    setPreset(activeFilter.preset)
    setCustomFrom(activeFilter.date_from || toDateInputValue(new Date()))
    setCustomTo(activeFilter.date_to || toDateInputValue(new Date()))
    setRangeError('')
  }, [showFilter, activeFilter])

  const handlePresetChange = (id: FeedbackDatePreset) => {
    setPreset(id)
    setRangeError('')
    if (id !== 'custom') {
      const range = getRangeForPreset(id)
      setCustomFrom(range.date_from)
      setCustomTo(range.date_to)
    }
  }

  const handleFilterData = (e: React.FormEvent) => {
    e.preventDefault()
    if (preset === 'custom' && customFrom && customTo && customFrom > customTo) {
      setRangeError('From date must be on or before To date.')
      return
    }
    const range = getRangeForPreset(
      preset,
      preset === 'custom' ? customFrom : undefined,
      preset === 'custom' ? customTo : undefined
    )
    onApply({...range})
    toggle()
  }

  return (
    <Modal show={showFilter} className='modal-md' onHide={toggle}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Filter feedback</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleFilterData}>
          <label className='form-label fw-semibold'>Date range</label>
          <select
            className='form-select mb-4'
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value as FeedbackDatePreset)}
          >
            {FEEDBACK_DATE_PRESET_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>

          {preset === 'custom' && (
            <div className='row g-4 mb-4'>
              <div className='col-6'>
                <label className='form-label'>From</label>
                <input
                  type='date'
                  className='form-control'
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => {
                    setCustomFrom(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              <div className='col-6'>
                <label className='form-label'>To</label>
                <input
                  type='date'
                  className='form-control'
                  value={customTo}
                  min={customFrom || undefined}
                  max={toDateInputValue(new Date())}
                  onChange={(e) => {
                    setCustomTo(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              {rangeError ? (
                <div className='col-12 text-danger fs-7'>{rangeError}</div>
              ) : null}
            </div>
          )}

          <Modal.Footer className='px-0 pb-0 mt-5'>
            <Button variant='secondary' size='sm' onClick={toggle}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' size='sm'>
              Apply filter
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default FeedbackFilter
